import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const activate = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      api.get('/api/agent/daily-brief', { params: { timezone: tz } }).catch(() => {});
    } catch {
      /* ignore */
    }
  }, []);

  const deactivate = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      // Profile uses authenticateToken middleware: expired access + valid refresh cookie is renewed inline (no POST /refresh race).
      const response = await api.get('/api/auth/profile');
      activate(response.data.user);
    } catch (err) {
      // Only deactivate on auth errors (401/403) — not on network/server errors
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        deactivate();
      }
    } finally {
      setLoading(false);
    }
  }, [activate, deactivate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const handleAuthExpired = () => {
      deactivate();
    };
    window.addEventListener('authExpired', handleAuthExpired);
    return () => window.removeEventListener('authExpired', handleAuthExpired);
  }, [deactivate]);

  const login = async (email, password) => {
    await api.post('/api/auth/login', { email, password });
    await fetchProfile();
    return { message: 'Login successful' };
  };

  const register = async (email, password, name, role, countryId, birthdate) => {
    await api.post('/api/auth/register', { email, password, name, role, countryId, birthdate });
    const profileRes = await api.get('/api/auth/profile');
    activate(profileRes.data.user);
    setLoading(false);
    return profileRes.data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Clear state regardless
    }
    deactivate();
  };

  const updateProfile = async (data) => {
    const response = await api.put('/api/auth/profile', data);
    await fetchProfile();
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        updateProfile,
        fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
